<template>
  <div id="map-container">
    <div id="map" style="height: 600px;"></div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import * as L from 'leaflet'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster'
import { icon } from "leaflet";
import { getFilePath } from "@/core/helpers/assets";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

export default {
  name: 'LeafletMap',
    props: {
      listMarker: {
        type: Array,
        required: true,
    },
  },
  setup(props) {
    const map = ref(null)
    const markerClusterGroup = ref(null)
    const isMapInitialized = ref(false)
    const currentZoom = ref(13)
    const clusterCount = ref(0)
    const debug = ref(false) // Set false untuk production

    watch(() => props.listMarker, (newMarkers, oldMarkers) => {
      if (isMapInitialized.value && newMarkers !== oldMarkers) {
        setTimeout(() => {
          cleanupMap()
        }, 100)
      }
    }, { deep: true })

    const initializeMap = async () => {
      try {
        await nextTick()
        
        const mapElement = document.getElementById('map')
        if (!mapElement) {
          console.error('Map element not found')
          return
        }

        // Initialize map dengan options yang lebih robust
        map.value = L.map('map', {
          preferCanvas: true,
          zoomControl: true,
          maxZoom: 20,
          minZoom: 1,
          zoomSnap: 0.5,
          zoomDelta: 0.5,
          wheelPxPerZoomLevel: 60
        }).setView([-6.2, 106.816666], 12)

        // Tile layer dengan error handling
        const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
          detectRetina: true,
          errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
        }).addTo(map.value)

        osmLayer.on('tileerror', (error) => {
          console.warn('Tile loading error:', error)
        })

        // Tunggu map ready
        await new Promise(resolve => {
          map.value.whenReady(resolve)
        })

        // Initialize marker cluster dengan options yang diperbaiki
        initializeMarkerCluster()

        // Setup event listeners
        setupMapEvents()

        isMapInitialized.value = true
        console.log('Map initialized successfully')

      } catch (error) {
        console.error('Error initializing map:', error)
      }
    }

    const initializeMarkerCluster = () => {
      // Hapus existing cluster group jika ada
      if (markerClusterGroup.value) {
        try {
          markerClusterGroup.value.clearLayers()
          markerClusterGroup.value.removeFrom(map.value)
        } catch (e) {
          console.warn('Error removing existing cluster group:', e)
        }
      }

      // Buat marker cluster group baru dengan options yang aman
      markerClusterGroup.value = L.markerClusterGroup({
        chunkedLoading: true,
        chunkDelay: 1000,
        animate: false,
        animateAddingMarkers: true,
        maxClusterRadius: 60, // Diperkecil untuk menghindari issue
        spiderfyOnMaxZoom: false, // Nonaktifkan spiderfy pada max zoom
        showCoverageOnHover: true, // Sementara nonaktifkan
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 17,
        removeOutsideVisibleBounds: true,
        singleMarkerMode: false,
        
        // Custom icon function untuk menghindari null reference
        iconCreateFunction: function(cluster) {
          const count = cluster.getChildCount()
          let size = 'small'
          
          if (count > 50) size = 'large'
          else if (count > 10) size = 'medium'
          
          return L.divIcon({
            html: '<div><span>' + count + '</span></div>',
            className: 'marker-cluster marker-cluster-' + size,
            iconSize: L.point(40, 40)
          })
        }
      })

      // Tambahkan markers contoh
      addMarkers()

      // Tambahkan cluster group ke map dengan delay kecil
      setTimeout(() => {
        if (map.value && markerClusterGroup.value) {
          map.value.addLayer(markerClusterGroup.value)
          clusterCount.value = markerClusterGroup.value.getLayers().length
          console.log('Markers added to cluster:', clusterCount.value)
        }
      }, 100)
    }

    // Fungsi clear markers
    const clearMarkers = () => {
        if (markerClusterGroup.value) {
            markerClusterGroup.value.clearLayers()
        }
    }

    const addMarkers = () => {

        if (!markerClusterGroup.value) {
            initializeMarkerCluster()
        }

            clearMarkers()

      props.listMarker.forEach((loc, index) => {
        try {

          //custom icon
          const customIcon = getIcon(loc.owner_spklu.icon)

          const marker = L.marker([loc.latitude, loc.longitude], {
            title: loc.nama,
            icon: customIcon
          })
          
          marker.bindPopup(`
            <table class="width:100%" border="0">
                <tr>
                    <th colspan="2">
                        <h5>
                            <strong>${loc.nama}</strong>
                        </h5>
                    </th>
                </tr>
                <tr>
                    <th>Owner / Operator</th>
                    <td>${loc.owner_spklu.nama}</td>
                </tr>
                <tr>
                    <th>Teknologi</th>
                    <td>${loc.teknologi ?? '-'}</td>
                </tr>
                <tr>
                    <th>Plug</th>
                    <td>${loc.plug ?? '-'}</td>
                </tr>
                <tr>
                    <th>Maksimal Daya</th>
                    <td>${loc.informasi_daya ?? '-'}</td>
                </tr>
                <tr>
                    <th>Jumlah Stasiun Pengisian</th>
                    <td>${loc.jumlah_stasiun_pengisian ?? '-'}</td>
                </tr>
                <tr>
                <td colspan="2">
                  ${loc.alamat} <br />
                    <br />
                    <a
                        target="_blank"
                        href="http://maps.google.com/maps?q=${loc.latitude},${loc.longitude}"
                        style="padding: 7px; margin-top: 10px"
                        class="bg-custom-secondary text-white w-100"
                        >Menuju Lokasi
                        <i class="bi bi-arrow-up-right-square-fill"></i
                    ></a>
                    </td>
                </tr>
            </table>
          `)
          
          // Tambahkan event listener yang aman
          marker.on('click', (e) => {
            e.originalEvent?.stopPropagation()
          })
          
          markerClusterGroup.value.addLayer(marker)
        } catch (error) {
          console.warn('Error adding marker:', error)
        }
      })
    }

    const setupMapEvents = () => {
      map.value.closePopup()
      if (!map.value) return

      let moveTimeout: NodeJS.Timeout | null = null
      let isMoving = false

      // Zoom events
      map.value.on('zoomstart', (e) => {
        console.log('Zoom start')
        // Nonaktifkan sementara cluster animation selama zoom
        if (markerClusterGroup.value) {
          markerClusterGroup.value.options.animate = false
        }
      })

      map.value.on('zoomend', (e) => {
        map.value.closePopup()
        currentZoom.value = map.value.getZoom()
        console.log('Zoom end:', currentZoom.value)
        
        // Aktifkan kembali animation setelah zoom selesai
        setTimeout(() => {
          if (markerClusterGroup.value) {
            markerClusterGroup.value.options.animate = false
          }
        }, 3000)
      })

      // Move events
      map.value.on('move', (e) => {
          // Debounce untuk event move yang terus menerus
          if (moveTimeout) {
            clearTimeout(moveTimeout)
          }
          
          moveTimeout = setTimeout(() => {
            console.log('Move debounced', map.value?.getCenter())
          }, 1000) // Jeda 100ms
        })

        map.value.on('moveend', (e) => {
          // Tunggu sebentar untuk pastikan benar-benar berhenti
          if (moveTimeout) {
            clearTimeout(moveTimeout)
          }
          
          moveTimeout = setTimeout(() => {
            isMoving = false
            console.log('Move end - Final position:', map.value?.getCenter())
            console.log('Zoom level:', map.value?.getZoom())
            
          }, 1000) // Jeda 150ms setelah moveend
        })

      // Error handling
      map.value.on('error', (e) => {
        console.error('Map error:', e)
      })

      // Cluster events
      if (markerClusterGroup.value) {
        markerClusterGroup.value.on('clusterclick', (e) => {
          map.value.closePopup()
          e.originalEvent?.stopPropagation()
        })

        markerClusterGroup.value.on('animationend', (e) => {
          map.value.closePopup()
          console.log('Cluster animation ended')
        })
      }
    }

    const safeZoom = (zoomLevel) => {
      if (!map.value) return
      
      try {
        // Nonaktifkan cluster animation sementara
        if (markerClusterGroup.value) {
          markerClusterGroup.value.options.animate = false
        }
        
        map.value.setZoom(zoomLevel, {
          animate: true,
          duration: 0.5
        })
        
        // Aktifkan kembali setelah zoom
        setTimeout(() => {
          if (markerClusterGroup.value) {
            markerClusterGroup.value.options.animate = true
          }
        }, 600)
      } catch (error) {
        console.error('Zoom error:', error)
      }
    }

    const cleanupMap = () => {
      if (map.value) {
        try {
          // Hapus cluster group terlebih dahulu
          if (markerClusterGroup.value) {
            // Remove event listeners dari cluster
            markerClusterGroup.value.off()
            markerClusterGroup.value.clearLayers()
            
            // Hapus dari map dengan safe check
            if (map.value.hasLayer(markerClusterGroup.value)) {
              map.value.removeLayer(markerClusterGroup.value)
            }
            
            markerClusterGroup.value = null
          }

          // Hapus semua layers
          map.value.eachLayer((layer) => {
            try {
              map.value.removeLayer(layer)
            } catch (e) {
              console.warn('Error removing layer:', e)
            }
          })

          // Hapus event listeners
          map.value.off()

          // Hapus map instance
          map.value.remove()
          map.value = null

          isMapInitialized.value = false
          console.log('Map cleanup completed')
        } catch (error) {
          console.warn('Error during map cleanup:', error)
        }
      }
    }

    onMounted(() => {
      initializeMap()
    })

    onBeforeUnmount(() => {
      cleanupMap()
    })

    const getIcon = (type: string) => {
      return new icon({
        iconUrl: getFilePath("owner-spklu", type),
        iconSize: [50, 55]
      });
    };

    return {
      map,
      isMapInitialized,
      currentZoom,
      clusterCount,
      debug,
      safeZoom,
      getIcon
    }
  }
}
</script>

<style scoped>
#map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

#map {
  height: 600px;
  width: 100%;
  z-index: 1;
}

.debug-info {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
}

/* Improved cluster styles */
:deep(.marker-cluster) {
  background-clip: padding-box;
  border-radius: 20px;
}

:deep(.marker-cluster div) {
  width: 30px;
  height: 30px;
  margin-left: 5px;
  margin-top: 5px;
  text-align: center;
  border-radius: 15px;
  font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
  font-weight: bold;
}

:deep(.marker-cluster span) {
  line-height: 30px;
}

:deep(.marker-cluster-small) {
  background-color: rgba(181, 226, 140, 0.7);
}

:deep(.marker-cluster-small div) {
  background-color: rgba(110, 204, 57, 0.7);
  color: white;
}

:deep(.marker-cluster-medium) {
  background-color: rgba(241, 211, 87, 0.7);
}

:deep(.marker-cluster-medium div) {
  background-color: rgba(240, 194, 12, 0.7);
  color: white;
}

:deep(.marker-cluster-large) {
  background-color: rgba(253, 156, 115, 0.7);
}

:deep(.marker-cluster-large div) {
  background-color: rgba(241, 128, 23, 0.7);
  color: white;
}

/* Fix untuk leaflet default styles */
:deep(.leaflet-container) {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
}
</style>